import { Form } from 'react-final-form';
import { Box, lighten } from '@mui/material';

import { ConfirmDialog, LinearProgress, Link, Typography } from '@occc-app/design-system';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { CreateRetailerNetworkLocationGroups } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { LocationGroupSelect } from './location-group-select.component';
import { AddLocationsToLocationGroupDialogFormValues } from './add-locations-to-location-group-dialog.types';

type AddLocationsToLocationGroupDialog = {
  open: boolean;
  onClose: () => void;
  onCreateNewGroupClick: () => void;
  onSubmit: (values: AddLocationsToLocationGroupDialogFormValues) => Promise<void>;
  loading: boolean;
  bulkSelectedItems: number;
  retailerNetworkUuid: string;
};

export const AddLocationsToLocationGroupDialog = ({
  open,
  onClose,
  onCreateNewGroupClick,
  onSubmit,
  loading,
  bulkSelectedItems,
  retailerNetworkUuid,
}: AddLocationsToLocationGroupDialog) => {
  const dialogTitle = `Add ${bulkSelectedItems} Location${
    bulkSelectedItems > 1 ? 's' : ''
  } to an Existing Location Group`;

  const renderForm = () => (
    <>
      <Typography variant='subtitle1'>
        Select a location group where you would like to add selected location
      </Typography>

      {loading && <LinearProgress />}

      <Box mt={3} mb={1}>
        <LocationGroupSelect retailerNetworkUuid={retailerNetworkUuid} />
      </Box>

      <Can I={CreateRetailerNetworkLocationGroups({ uuid: retailerNetworkUuid })}>
        <Box
          p={2}
          borderRadius={1}
          sx={({ palette }) => ({ background: lighten(palette.primary.main, 0.9) })}
        >
          <Typography>
            Or you can add this location to a{' '}
            <Link
              component='span'
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                onClose();
                onCreateNewGroupClick();
              }}
            >
              New Location Group
            </Link>
          </Typography>
        </Box>
      </Can>
    </>
  );

  return (
    <Form<AddLocationsToLocationGroupDialogFormValues>
      onSubmit={onSubmit}
      render={({ handleSubmit, values }) => (
        <ConfirmDialog
          confirmButtonText='Add to a group'
          confirmButtonProps={{
            onClick: handleSubmit,
            disabled: !values.locationGroup?.uuid || loading,
          }}
          dialogTitle={dialogTitle}
          open={open}
          onClose={onClose}
        >
          {renderForm()}
        </ConfirmDialog>
      )}
    />
  );
};
